import * as HoverCard from '@radix-ui/react-hover-card';
import { useRef, useState, type PointerEvent } from 'react';
import { cn } from '@/lib/utils';
import type { MatterMember } from '@/types/base';

const avatarColors = [
  'bg-blue-400/25',
  'bg-emerald-500/25',
  'bg-violet-400/25',
  'bg-amber-500/25',
  'bg-rose-400/25',
  'bg-cyan-500/25',
];

interface MatterMemberAvatarsProps {
  members: MatterMember[];
  maxVisible?: number;
  /** When members is a truncated preview, pass the full team size for the +N badge. */
  totalCount?: number;
  title?: string;
  emptyLabel?: string;
}

function getInitials(name: string): string {
  const label = name.trim() || '?';
  const parts = label.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  return label.slice(0, 2).toUpperCase();
}

function getAvatarColor(key: string): string {
  const hash = key.split('').reduce((total, char) => total + char.charCodeAt(0), 0);

  return avatarColors[hash % avatarColors.length];
}

export function MatterMemberAvatars({
  members,
  maxVisible = 3,
  totalCount,
  title = 'Matter team',
  emptyLabel = '-',
}: MatterMemberAvatarsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isTouchOpenRef = useRef(false);

  if (!members.length) {
    return <span className="text-sm text-muted-foreground">{emptyLabel}</span>;
  }

  const teamSize = Math.max(totalCount ?? members.length, members.length);
  const totalVisibleSlots = Math.max(1, maxVisible);
  const needsOverflow = teamSize > totalVisibleSlots;
  const visibleMemberCount = needsOverflow ? totalVisibleSlots - 1 : totalVisibleSlots;
  const visibleMembers = members.slice(0, visibleMemberCount);
  const remainingCount = teamSize - visibleMembers.length;

  const handleTriggerPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }

    if (event.pointerType === 'touch') {
      isTouchOpenRef.current = true;
      setIsOpen(true);
    }
  };

  const closeCard = () => {
    isTouchOpenRef.current = false;
    setIsOpen(false);
  };

  return (
    <HoverCard.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          setIsOpen(true);
          return;
        }

        if (!isTouchOpenRef.current) {
          setIsOpen(false);
        }
      }}
      openDelay={0}
      closeDelay={100}
    >
      <HoverCard.Trigger asChild>
        <button
          type="button"
          aria-label={`Show ${teamSize} matter team member${teamSize === 1 ? '' : 's'}`}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          className="flex h-8 items-center -space-x-2 rounded-full outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onPointerDown={handleTriggerPointerDown}
          onClick={(event) => event.stopPropagation()}
        >
          {visibleMembers.map((member, index) => (
            <span
              key={member.user_guid}
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-white text-[11px] font-semibold leading-none text-stone-800',
                getAvatarColor(member.user_guid)
              )}
              style={{ zIndex: visibleMembers.length - index + 1 }}
            >
              {getInitials(member.name)}
            </span>
          ))}
          {remainingCount > 0 && (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-white bg-stone-200 text-[11px] font-semibold leading-none text-stone-700"
              style={{ zIndex: 1 }}
            >
              +{remainingCount}
            </span>
          )}
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          className="z-100000 w-72 rounded-md border border-stone-200 bg-white p-0 text-stone-950 shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          align="start"
          sideOffset={4}
          onEscapeKeyDown={closeCard}
          onPointerDownOutside={closeCard}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-stone-200 px-3 py-2">
            <p className="text-sm font-medium text-stone-900">{title}</p>
            <p className="text-xs text-stone-500">
              {teamSize} active member{teamSize === 1 ? '' : 's'}
              {teamSize > members.length ? ' (preview)' : ''}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {members.map((member) => (
              <div key={member.user_guid} className="flex items-center gap-3 px-3 py-2">
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-stone-800',
                    getAvatarColor(member.user_guid)
                  )}
                >
                  {getInitials(member.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-stone-900">{member.name}</p>
                  <p className="truncate text-xs text-stone-500">{member.email}</p>
                </div>
                <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">
                  {member.role}
                </span>
              </div>
            ))}
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
